// ─── Talking to a NULIFE server ──────────────────────────────────────────────
//
// Extracted from Crafting Comb's own `client/api.ts` on 2026-09-15, when the
// Memorial Hall committee app became the second product that needed it. What
// moved is the transport and the error behaviour; what stayed in each app is
// the routes and the shapes, because those genuinely differ.
//
// ── Why the error handling is the part worth sharing ────────────────────────
//
// Three of the decisions below are this platform's founding requirement in
// miniature, and each of them is easy to get wrong in a way that produces a
// confident screen showing something untrue:
//
//   A dropped connection is NOT a server error, and must not be reported as
//   "your booking failed" when nobody knows whether it did.
//
//   The server's own message survives. It says a real reason for the cases a
//   person can act on — a slot that has just gone, a name that is missing —
//   and replacing those with "Something went wrong" removes the only thing
//   that says what to do next.
//
//   A 401 is announced, so a session that ended mid-use returns the app to the
//   sign-in form rather than leaving a screenful of broken panels.
//
// A second copy of that would be a second place for one of them to be quietly
// simplified.

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
  }
  /** The thing was taken while they were filling in the form. Worth its own
   *  check: it is the one failure where the right response is to show what is
   *  left, not to retry. */
  get slotTaken(): boolean {
    return this.status === 409;
  }
}

export type ApiClient = {
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
  post: <T>(path: string, body: unknown) => Promise<T>;
  /**
   * The raw response, for a request `request()` cannot make.
   *
   * Two real cases: a multipart body must NOT carry
   * `Content-Type: application/json`, which `request()` always sets whenever a
   * body is present; and a route where a particular status is an ANSWER rather
   * than a failure — a 404 from "save these notes" means "nothing changed",
   * which the caller needs as a value, not as a throw.
   *
   * It still does the two things that must not be skipped: a dropped
   * connection becomes an `ApiError` rather than a raw `TypeError`, and a 401
   * is announced. Before this existed, each bypass path was hand-written, and
   * two of the three in Crafting Comb never announced the 401 — so a session
   * that ended while somebody was attaching a file or saving a visit note left
   * a raw error on the screen instead of returning them to the sign-in form.
   *
   * The caller checks `res.ok` itself. That is the point of it.
   */
  send: (path: string, init?: RequestInit) => Promise<Response>;
  /** Called when the server says 401 — a session ended, or somebody signed out
   *  in another tab. */
  whenSignedOut: (handler: () => void) => void;
};

/**
 * A client for one server.
 *
 * `base` is the API prefix. Both products mount their router at `/api` on
 * their own origin, so there is one default and no CORS to configure; it is a
 * parameter rather than a constant because a product that ever moves its API
 * should change one line, not fork this file.
 */
export function createApiClient(base = "/api"): ApiClient {
  let onSignedOut: (() => void) | undefined;

  async function send(path: string, init?: RequestInit): Promise<Response> {
    let res: Response;
    try {
      // No `credentials` default. `base` is a relative path, so every request
      // is same-origin and the browser sends the cookie anyway; setting
      // "include" would additionally send it to any absolute base somebody
      // pointed this at later, which is a decision that should be made out
      // loud at the call site rather than inherited from here.
      res = await fetch(`${base}${path}`, init);
    } catch {
      // A dropped connection is not a server error, and saying so lets the
      // person try again rather than assume what they did failed.
      throw new ApiError(0, "Could not reach the server. Check your connection and try again.");
    }
    // The session went — ended elsewhere, or a password change closed it. Told
    // to whoever is listening so the app can show the sign-in form again
    // rather than a screenful of failed panels. Announced HERE, so every
    // caller gets it including the ones that read the response themselves.
    if (res.status === 401) onSignedOut?.();
    return res;
  }

  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const res = await send(path, {
      ...init,
      headers: init?.body ? { "Content-Type": "application/json" } : undefined,
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      throw new ApiError(res.status, (body as { error?: string })?.error ?? "Something went wrong.");
    }
    return body as T;
  }

  return {
    request,
    post: <T>(path: string, body: unknown) =>
      request<T>(path, { method: "POST", body: JSON.stringify(body) }),
    send,
    whenSignedOut(handler) { onSignedOut = handler; },
  };
}
